import { BundleType } from '@peach/types/bundles/types.js';
import { PRICE_MIN } from '@peach/types/payments/price-limits.js';
import {
  CLAIM_TRIAL_DURATION,
  DEFAULT_CLAIM_TRIAL_PRICE,
} from '@peach/types/tutors/claim-page.consts.js';
import type { BundleDraft } from './bundle-config';

/**
 * A product's price is what its owner gets, and nothing on the page moves it.
 * "Make it a deal" only shows an old price above it, struck through.
 */

type Terms = Pick<BundleDraft, 'id' | 'type' | 'numberOfClasses' | 'duration' | 'price'>;

/** The first one-call package, which a newly added pack takes its price per minute from. */
export function referenceCall<T extends Pick<Terms, 'type' | 'numberOfClasses'>>(
  bundles: T[]
): T | undefined {
  return bundles.find((b) => b.type === BundleType.ONE_ON_ONE && b.numberOfClasses === 1);
}

/**
 * The price per minute a new product is priced from: the single call, else the
 * intro, else the default intro — so the intro's price is the page's one knob.
 */
export function referenceRate(bundles: Terms[]): number {
  const ref = referenceCall(bundles) ?? bundles.find((b) => b.type === BundleType.TRIAL);
  return ref
    ? perMinute(ref)
    : perMinute({
        numberOfClasses: 1,
        duration: CLAIM_TRIAL_DURATION,
        price: DEFAULT_CLAIM_TRIAL_PRICE,
      });
}

/** A product's own price per minute. */
function perMinute(b: Pick<Terms, 'numberOfClasses' | 'duration' | 'price'>): number {
  return b.price / (b.numberOfClasses * b.duration);
}

/**
 * How far under the owner's usual price a new product starts: a quarter off
 * the intro, and a pack's growing with its size, 10% from four calls and 5 more
 * for every four after, up to 30%. One to three calls start at the usual price.
 */
export function dealDiscount(b: Pick<Terms, 'type' | 'numberOfClasses'>): number {
  if (b.type === BundleType.TRIAL) return 25;
  if (b.numberOfClasses < 4) return 0;
  return Math.min(30, 10 + 5 * Math.floor((b.numberOfClasses - 4) / 4));
}

/** A new product's price at this price per minute, under it by its starting discount. Whole dollars. */
export function startingPrice(
  b: Pick<Terms, 'type' | 'numberOfClasses' | 'duration'>,
  rate: number
): number {
  const full = rate * b.duration * b.numberOfClasses;
  return Math.max(PRICE_MIN, Math.round(full * (1 - dealDiscount(b) / 100)));
}

/** The old price a deal shows: a quarter above the price, so the card reads "save 20%". */
export function oldPrice(price: number): number {
  return Math.max(price + 1, Math.round(price * 1.25));
}

/**
 * The old price a deal shows at `price`, keeping the owner's own discount when
 * `from` carries one: 41 live products had a hand-set old price, and a change
 * of calls or length must not quietly turn it into our quarter.
 */
export function dealPrice(
  from: { price: number; originalPrice?: number } | undefined,
  price: number
): number {
  if (!from?.originalPrice || from.originalPrice <= from.price) return oldPrice(price);
  if (price === from.price) return from.originalPrice;
  return Math.max(price + 1, Math.round((price * from.originalPrice) / from.price));
}

/**
 * The struck price the deal switch names while a product is edited: its own
 * deal when on, else the deal it was saved with, so off and on again gets it back.
 */
export function shownDealPrice(
  draft: { deal: boolean; price: number; originalPrice?: number },
  saved: { price: number; originalPrice?: number } | undefined
): number {
  return dealPrice(draft.deal ? draft : saved, draft.price);
}
